import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Check, Plus } from 'lucide-react';
import { getProductIncludedSummary } from '@/lib/productComposition';
import { onDemoProductImageError } from '@/lib/demo/demoBusiness';

export function AssistantProductCard({
  product,
  primaryColor,
  lineQty,
  inCartQty = 0,
  onBumpQty,
  onAdd,
  variant = 'catalog',
  isConfirmation = false,
  isWelcomeClubReward = false,
  maxRewardQty = 99,
  business,
  testId,
  addLabel = 'Agregar',
}) {
  const price = Number(product.discount_price || product.price) || 0;
  const displayPrice = isWelcomeClubReward ? 0 : price;
  const incLine = getProductIncludedSummary(product);
  const dashed = variant === 'suggested';

  return (
    <Card className={`p-4 flex gap-3${dashed ? ' border-dashed' : ''}`}>
      {product.photo_url ? (
        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={product.photo_url}
            alt={product.name}
            className="w-full h-full object-cover"
            onError={(e) => onDemoProductImageError(e, business)}
          />
        </div>
      ) : null}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="font-semibold text-sm">{product.name}</h4>
          {isWelcomeClubReward ? (
            <Badge variant="secondary" className="text-[10px] shrink-0">
              Premio club
            </Badge>
          ) : null}
        </div>
        {incLine ? (
          <p className="text-xs text-primary/90 line-clamp-2 mt-1">Incluye: {incLine}</p>
        ) : product.description ? (
          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{product.description}</p>
        ) : null}
        {inCartQty > 0 && !isConfirmation ? (
          <p className="text-xs text-muted-foreground mt-1">
            En el carrito: {inCartQty}
            {inCartQty === 1 ? ' unidad' : ' unidades'}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center justify-between mt-2 gap-2">
          <span className="font-bold text-sm" style={{ color: primaryColor }}>
            {isWelcomeClubReward ? (
              <span className="text-muted-foreground">Canje sin cargo</span>
            ) : (
              <>
                ${displayPrice.toLocaleString('es-CL')}
                {lineQty > 1 ? (
                  <span className="font-normal text-muted-foreground text-xs ml-1">
                    ×{lineQty} = ${(displayPrice * lineQty).toLocaleString('es-CL')}
                  </span>
                ) : null}
              </>
            )}
          </span>
          {isConfirmation ? (
            <Badge
              variant="secondary"
              className="shrink-0 gap-1 font-normal"
              style={{ borderColor: `${primaryColor}40` }}
            >
              <Check className="w-3.5 h-3.5" style={{ color: primaryColor }} />
              Agregado
            </Badge>
          ) : (
            <div className="flex items-center gap-1.5 shrink-0">
              <div className="flex items-center border rounded-md bg-background">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-r-none"
                  disabled={lineQty <= 1}
                  onClick={() => onBumpQty(-1)}
                  aria-label="Menos cantidad"
                >
                  -
                </Button>
                <span className="text-sm tabular-nums w-8 text-center">{lineQty}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-l-none"
                  disabled={isWelcomeClubReward ? lineQty >= maxRewardQty : lineQty >= 99}
                  onClick={() => onBumpQty(1)}
                  aria-label="Más cantidad"
                >
                  +
                </Button>
              </div>
              <Button
                size="sm"
                onClick={onAdd}
                disabled={isWelcomeClubReward && maxRewardQty < 1}
                style={{ backgroundColor: primaryColor }}
                className="text-white h-8 px-3"
                data-testid={testId}
              >
                <Plus className="w-4 h-4 mr-1" />
                {addLabel}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
